import React from 'react';

// Color presets for each level
const COLOR_PRESETS = {
  '#60B072': { stroke: '#86D99B', ringFill: '#B8E8C8', dataFill: '#86D99B', pointFill: '#60B072' },
  '#83C346': { stroke: '#BEE486', ringFill: '#BEE486', dataFill: '#A5D65C', pointFill: '#83C346' },
  '#F2C014': { stroke: '#F5D76E', ringFill: '#F5D76E', dataFill: '#F5D044', pointFill: '#F2C014' },
  '#FD9153': { stroke: '#FFBA8F', ringFill: '#FFBA8F', dataFill: '#FF9E6B', pointFill: '#FD9153' },
  '#F85269': { stroke: '#FF8FA0', ringFill: '#FF8FA0', dataFill: '#FF6B80', pointFill: '#F85269' },
};

/**
 * GutRadarChart - Custom SVG Pentagon Radar Chart
 * Uses exact pentagon paths from Figma design.
 * Color changes based on the overall score level.
 *
 * @param {Object} props
 * @param {Array} props.labels - Category labels (5 items)
 * @param {Array} props.scores - Raw scores for each category
 * @param {Array} props.maxScores - Max possible score per category
 * @param {string} props.color - Level color (e.g. '#83C346', '#F85269')
 */
const GutRadarChart = ({ labels = [], scores = [], maxScores = [], color = '#83C346' }) => {
  const colors = COLOR_PRESETS[color] || COLOR_PRESETS['#83C346'];

  // Center of the pentagon in the SVG viewBox
  const cx = 86.5;
  const cy = 78;

  // The outermost ring path has 5 straight edges connected by bezier curves at corners.
  // Edge segments (from the path 'd' attribute):
  //   Edge 1 (top-right): (91.9551, 5.9375) → (159.694, 50.0322)
  //   Edge 2 (right):     (163.64, 61.8223) → (138.331, 131.608)
  //   Edge 3 (bottom):    (128.931, 138.199) → (44.0693, 138.199)
  //   Edge 4 (left):      (34.6689, 131.608) → (9.36035, 61.8223)
  //   Edge 5 (top-left):  (13.3057, 50.0322) → (81.0449, 5.9375)
  //
  // The 5 vertices are the intersections of adjacent edge lines:
  // Vertex 0 (top): intersection of Edge 5 and Edge 1
  //   Edge 5 direction: (81.04-13.31, 5.94-50.03) = (67.73, -44.09)
  //   Edge 1 direction: (159.69-91.96, 50.03-5.94) = (67.73, 44.09)
  //   Both pass through y≈5.94 at x≈86.5 → top vertex = (86.5, 5.94)
  //
  // Vertex 1 (top-right): intersection of Edge 1 and Edge 2
  //   Edge 1: from (91.96, 5.94), slope = 44.09/67.73 = 0.651
  //   Edge 2: from (163.64, 61.82) to (138.33, 131.61), slope = 69.79/(-25.31) = -2.757
  //   Solving: vertex ≈ (163.64, 52.67) → but with rounded corner, effective ≈ (161.5, 56)
  //
  // For a radar chart with rounded corners, the effective 100% point should be
  // on the midpoint of each straight edge (where the axis from center hits the edge).
  // Each axis goes from center at angle: -90°, -18°, 54°, 126°, 198° (pentagon angles)
  
  // Midpoints of each straight edge segment:
  const edgeMidpoints = [
    { x: (91.9551 + 81.0449) / 2, y: (5.9375 + 5.9375) / 2 },         // top edge midpoint - but this is the curve, not an edge!
  ];
  
  // Actually, let me reconsider. The path structure is:
  // M81.04 5.94 C(curve for top corner) L159.69 50.03 C(curve for top-right corner) L138.33 131.61 C(curve for bottom-right corner) H44.07 C(curve for bottom-left corner) L9.36 61.82 C(curve for top-left corner) L81.04 5.94 Z
  // 
  // So the CORNERS have the curves, and the EDGES are straight lines between them.
  // The 5 axes of the radar should point to the 5 corners.
  // The midpoint of each corner curve is the effective vertex position.
  //
  // Corner 0 (top): bezier from M(81.04, 5.94) with control points (84.36, 3.78)(88.64, 3.78) to (91.96, 5.94)
  //   Midpoint at t=0.5: x = 0.125*81.04 + 0.375*84.36 + 0.375*88.64 + 0.125*91.96 = 86.5
  //                       y = 0.125*5.94 + 0.375*3.78 + 0.375*3.78 + 0.125*5.94 = 4.32
  //
  // Corner 1 (top-right): bezier from (159.69, 50.03) with CP (163.59, 52.57)(165.22, 57.45) to (163.64, 61.82)
  //   Midpoint: x = 0.125*159.69 + 0.375*163.59 + 0.375*165.22 + 0.125*163.64 = 163.73
  //             y = 0.125*50.03 + 0.375*52.57 + 0.375*57.45 + 0.125*61.82 = 55.26
  //
  // Corner 2 (bottom-right): bezier from (138.33, 131.61) with CP (136.90, 135.56)(133.14, 138.20) to (128.93, 138.20)
  //   Midpoint: x = 0.125*138.33 + 0.375*136.90 + 0.375*133.14 + 0.125*128.93 = 134.28
  //             y = 0.125*131.61 + 0.375*135.56 + 0.375*138.20 + 0.125*138.20 = 136.32
  //
  // Corner 3 (bottom-left): bezier from (44.07, 138.20) with CP (39.86, 138.20)(36.10, 135.56) to (34.67, 131.61)
  //   Midpoint: x = 0.125*44.07 + 0.375*39.86 + 0.375*36.10 + 0.125*34.67 = 38.37
  //             y = 0.125*138.20 + 0.375*138.20 + 0.375*135.56 + 0.125*131.61 = 136.32
  //
  // Corner 4 (top-left): bezier from (9.36, 61.82) with CP (7.78, 57.45)(9.41, 52.57) to (13.31, 50.03)
  //   Midpoint: x = 0.125*9.36 + 0.375*7.78 + 0.375*9.41 + 0.125*13.31 = 9.28
  //             y = 0.125*61.82 + 0.375*57.45 + 0.375*52.57 + 0.125*50.03 = 55.26
  
  const outerVertices = [
    { x: 86.5, y: 4.32 },     // top center
    { x: 163.73, y: 55.26 },  // top-right
    { x: 134.28, y: 136.32 }, // bottom-right
    { x: 38.37, y: 136.32 },  // bottom-left
    { x: 9.28, y: 55.26 },    // top-left
  ];

  // Calculate data polygon points based on score percentages
  const dataPoints = scores.map((score, i) => {
    const max = maxScores[i] || 1;
    const pct = Math.min(score / max, 1);
    return {
      x: cx + (outerVertices[i].x - cx) * pct,
      y: cy + (outerVertices[i].y - cy) * pct,
    };
  });

  const dataPath = dataPoints.length > 0
    ? dataPoints.map((p, i) => `${i === 0 ? 'M' : 'L'}${p.x.toFixed(1)} ${p.y.toFixed(1)}`).join(' ') + ' Z'
    : '';

  // Label positions (offset further out from outer vertices)
  const labelOffsets = [
    { x: 86.5, y: -14 },   // top - above
    { x: 174, y: 56 },     // top-right - to the right
    { x: 148, y: 154 },    // bottom-right - below right
    { x: 25, y: 154 },     // bottom-left - below left
    { x: -4, y: 56 },      // top-left - to the left
  ];

  const textAnchors = ['middle', 'start', 'start', 'end', 'end'];

  return (
    <div className="gut-radar-chart">
      <svg
        width="100%"
        viewBox="-30 -30 240 205"
        fill="none"
        xmlns="http://www.w3.org/2000/svg"
        style={{ overflow: 'visible' }}
      >
        {/* Ring 1 - Outermost stroke */}
        <path
          d="M81.0449 5.9375C84.3614 3.77887 88.6387 3.77887 91.9551 5.9375L159.694 50.0322C163.589 52.5679 165.224 57.453 163.64 61.8223L138.331 131.608C136.896 135.564 133.139 138.199 128.931 138.199H44.0693C39.8613 138.199 36.1037 135.564 34.6689 131.608L9.36035 61.8223C7.77584 57.453 9.41065 52.5679 13.3057 50.0322L81.0449 5.9375Z"
          stroke={colors.stroke}
          strokeWidth="4"
          fill="none"
        />

        {/* Ring 2 */}
        <path
          opacity="0.2"
          d="M80.426 13.305C84.4187 10.6903 89.5813 10.6903 93.574 13.305L151.351 51.14C156 54.1841 157.95 60.0213 156.066 65.2486L134.531 124.987C132.815 129.746 128.3 132.918 123.242 132.918H50.7583C45.7 132.918 41.1849 129.746 39.4694 124.987L17.934 65.2486C16.0496 60.0213 18.0003 54.1841 22.6488 51.14L80.426 13.305Z"
          fill={colors.ringFill}
        />

        {/* Ring 3 */}
        <path
          opacity="0.2"
          d="M80.4009 24.3451C84.4053 21.7085 89.5947 21.7085 93.5991 24.3451L139.986 54.8875C144.611 57.9328 146.552 63.7474 144.683 68.96L127.469 116.973C125.759 121.742 121.239 124.923 116.173 124.923H57.8273C52.7614 124.923 48.2411 121.742 46.5314 116.973L29.3172 68.96C27.4483 63.7474 29.389 57.9328 34.014 54.8875L80.4009 24.3451Z"
          fill={colors.ringFill}
        />

        {/* Ring 4 - Innermost */}
        <path
          opacity="0.2"
          d="M80.8865 37.3682C84.8975 34.7189 90.1025 34.7189 94.1135 37.3682L126.723 58.9064C131.334 61.9523 133.269 67.7539 131.409 72.9582L119.441 106.444C117.734 111.219 113.211 114.406 108.141 114.406H66.8591C61.7888 114.406 57.2656 111.219 55.5592 106.444L43.5908 72.9582C41.7307 67.7539 43.6656 61.9523 48.2772 58.9064L80.8865 37.3682Z"
          fill={colors.ringFill}
        />

        {/* Data polygon */}
        {dataPath && (
          <path
            d={dataPath}
            fill={colors.dataFill}
            opacity="0.8"
          />
        )}

        {/* Data points (circles on vertices) */}
        {dataPoints.map((point, i) => (
          <circle
            key={`point-${i}`}
            cx={point.x.toFixed(1)}
            cy={point.y.toFixed(1)}
            r={4}
            fill={colors.pointFill}
          />
        ))}

        {/* Labels */}
        {labels.map((label, i) => {
          const pos = labelOffsets[i];
          const lines = label.split('\n');

          return (
            <text
              key={`label-${i}`}
              x={pos.x}
              y={pos.y}
              textAnchor={textAnchors[i]}
              fill="#2C2C2C"
              fontSize="12"
              fontFamily="'Sukhumvit Tadmai', sans-serif"
              // fontWeight="600"
              style={{ lineHeight: '14px' }}
            >
              {lines.map((line, li) => (
                <tspan key={li} x={pos.x} dy={li === 0 ? 0 : 14}>
                  {line}
                </tspan>
              ))}
            </text>
          );
        })}
      </svg>
    </div>
  );
};

export default GutRadarChart;
